/**
 * components/Settings/AI/ProviderSelectionStep.tsx
 * Provider selection step for the AI setup wizard
 *
 * First step of the wizard — displays selectable cards for each supported
 * AI provider. Provider data (names, models, connection method) is driven
 * entirely by AI constants so new providers/models are reflected automatically.
 *
 * Work Unit: WU01 - AI Setup Wizard
 * Task: T02 - Provider Selection Step
 * Sprint: S02 - AI Setup & Settings UI
 * Project: P09 - AI Integration
 */

// 1. External dependencies
import React from 'react';
import { CheckCircle2, Globe, Server } from 'lucide-react';

// 2. Workspace packages
import { Card, CardBody } from '@enfomentis/ui';

// 3. Internal absolute imports
import type { AIProviderType } from '@/types/shared/AI';
import {
  AI_MODELS,
  AI_PROVIDER_DISPLAY_MAP,
  AI_PROVIDER_TYPES,
} from '@/constants/ai';

// ─── Constants ──────────────────────────────────────────────────────────────

/** Connection method descriptions per provider */
const CONNECTION_METHOD_MAP: Readonly<Record<AIProviderType, string>> = {
  openai: 'Direct connection from your browser',
  anthropic: 'Via secure proxy (CORS required)',
};

/** Icons per provider for connection method */
const CONNECTION_ICON_MAP: Readonly<
  Record<AIProviderType, React.ReactNode>
> = {
  openai: <Globe className="size-4 text-default-400" />,
  anthropic: <Server className="size-4 text-default-400" />,
};

// ─── Props ──────────────────────────────────────────────────────────────────

/**
 * Props for the Provider Selection Step
 *
 * @see ProviderSelectionStep
 */
export interface ProviderSelectionStepProps {
  /** Currently selected provider, or null if none selected */
  selectedProvider: AIProviderType | null;

  /** Called when the user selects a provider */
  onSelect: (provider: AIProviderType) => void;
}

// ─── Component ──────────────────────────────────────────────────────────────

/**
 * Provider Selection Step
 *
 * Displays a selectable card for each AI provider with:
 * - Provider display name
 * - Available models (from `AI_MODELS`)
 * - Connection method description
 * - Visual highlight and check icon for the selected card
 *
 * All data is sourced from AI constants — no hardcoded provider strings.
 *
 * @example
 * ```tsx
 * <ProviderSelectionStep
 *   selectedProvider={selectedProvider}
 *   onSelect={setSelectedProvider}
 * />
 * ```
 */
export const ProviderSelectionStep: React.FC<ProviderSelectionStepProps> = ({
  selectedProvider,
  onSelect,
}) => {
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Choose Your AI Provider</h3>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {AI_PROVIDER_TYPES.map((provider) => {
          const isSelected = selectedProvider === provider;
          const displayName = AI_PROVIDER_DISPLAY_MAP[provider];
          const models = AI_MODELS.filter((m) => m.provider === provider);
          const modelNames = models.map((m) => m.name).join(', ');
          const connectionMethod = CONNECTION_METHOD_MAP[provider];
          const connectionIcon = CONNECTION_ICON_MAP[provider];

          return (
            <Card
              key={provider}
              isPressable
              onPress={() => onSelect(provider)}
              aria-label={`Select ${displayName} as your AI provider`}
              className={`transition-colors ${
                isSelected
                  ? 'border-2 border-primary bg-primary-50/10'
                  : 'border-2 border-transparent'
              }`}
            >
              <CardBody className="gap-3 p-4">
                {/* Header: provider name + check icon */}
                <div className="flex items-center justify-between">
                  <span className="text-base font-semibold">{displayName}</span>
                  {isSelected && (
                    <CheckCircle2 className="size-5 text-primary" />
                  )}
                </div>

                {/* Models */}
                <div>
                  <span className="text-xs font-medium text-default-500">
                    Models
                  </span>
                  <p className="text-sm">{modelNames}</p>
                </div>

                {/* Connection method */}
                <div className="flex items-center gap-2">
                  {connectionIcon}
                  <span className="text-xs text-default-400">
                    {connectionMethod}
                  </span>
                </div>
              </CardBody>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
